import React from 'react';
import PropTypes from 'prop-types';
import { dynastyTheme, withAlpha } from '../../../../../services/colorService';
import { PlayerPhoto, TrendIndicator } from '../shared';
import { formatBattingAvg, formatERA } from '../../../../../services/statFormatUtils';

const C = dynastyTheme.tokens.colors;

/**
 * MobilePlayerTile Component
 * 
 * Main player tile with 3 variants for different contexts.
 * All variants are clickable and open the player modal.
 * 
 * @param {string} variant - Tile variant: "compact" | "standard" | "featured"
 * @param {string} playerId - Player ID
 * @param {object} playerData - Player data object
 * @param {function} onClick - Click handler (opens modal)
 * @param {boolean} showTrend - Whether to show trend indicator (default: true)
 * @param {boolean} showChart - Whether to show mini chart (featured only)
 */
const MobilePlayerTile = ({ 
  variant = 'standard',
  playerId,
  playerData,
  onClick,
  showTrend = true,
  showChart = false
}) => {
  if (!playerData) {
    return <div style={loadingStyle}>Loading...</div>;
  }

  const handleClick = () => {
    if (onClick) {
      onClick(playerId, playerData);
    }
  };

  // Route to correct variant
  switch (variant) {
    case 'compact':
      return <CompactTile data={playerData} onClick={handleClick} showTrend={showTrend} />;
    case 'featured':
      return <FeaturedTile data={playerData} onClick={handleClick} showTrend={showTrend} showChart={showChart} />;
    case 'standard':
    default:
      return <StandardTile data={playerData} onClick={handleClick} showTrend={showTrend} />;
  }
};

/**
 * COMPACT VARIANT (40px height)
 * For: Roster lists, lineup editor, trade center
 */
const CompactTile = ({ data, onClick, showTrend }) => {
  const { name, position, stats, trend, isPitcher } = data;

  // Format name as "Last, First Initial"
  const formatCompactName = (fullName) => {
    if (!fullName) return 'Unknown';
    const parts = fullName.split(' ');
    if (parts.length < 2) return fullName;
    const lastName = parts[parts.length - 1];
    const firstInitial = parts[0][0];
    return `${lastName}, ${firstInitial}`;
  };

  const compactName = formatCompactName(name);

  // Get 3-4 key stats based on player type
  const getCompactStats = () => {
    if (!stats) return [];
    
    if (isPitcher) {
      return [
        { label: 'ERA', value: formatERA(stats.era) || '--' },
        { label: 'K', value: stats.strikeouts_pitched || '--' },
        { label: 'W-L', value: stats.wins && stats.losses ? `${stats.wins}-${stats.losses}` : '--' }
      ];
    } else {
      return [
        { label: 'AVG', value: formatBattingAvg(stats.batting_avg) || '--' },
        { label: 'HR', value: stats.home_runs || '--' },
        { label: 'RBI', value: stats.rbi || '--' }
      ];
    }
  };

  const statsList = getCompactStats();

  const containerStyle = {
    display: 'flex',
    alignItems: 'center',
    height: '40px',
    padding: '0 12px',
    backgroundColor: C.surface[855],
    borderRadius: '6px',
    border: `1px solid ${C.coolGray[850]}`,
    gap: '8px',
    cursor: 'pointer',
    transition: 'all 100ms ease',
    width: '100%'
  };

  const nameStyle = {
    fontSize: '14px',
    fontWeight: '500',
    color: C.white,
    minWidth: '100px',
    flexShrink: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  };

  const positionStyle = {
    fontSize: '12px',
    fontWeight: '600',
    color: C.coolGray[400],
    minWidth: '30px',
    flexShrink: 0
  };

  const statsContainerStyle = {
    display: 'flex',
    gap: '12px',
    flex: 1,
    justifyContent: 'flex-start'
  };

  const statStyle = {
    fontSize: '14px',
    fontWeight: '500',
    color: C.white,
    whiteSpace: 'nowrap'
  };

  const trendContainerStyle = {
    marginLeft: 'auto',
    flexShrink: 0
  };

  return (
    <div 
      style={containerStyle}
      onClick={onClick}
      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = C.surface[900]}
      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = C.surface[855]}
    >
      <span style={nameStyle}>{compactName}</span>
      <span style={positionStyle}>{position || '--'}</span>
      <div style={statsContainerStyle}>
        {statsList.map((stat, idx) => (
          <span key={idx} style={statStyle}>
            {stat.value} {stat.label}
          </span>
        ))}
      </div>
      {showTrend && trend && (
        <div style={trendContainerStyle}>
          <TrendIndicator 
            trend={trend.status || 'neutral'}
            percentage={trend.percentage || 0}
            size="compact"
          />
        </div>
      )}
    </div>
  );
};

/**
 * STANDARD VARIANT (100px height)
 * For: Search results, free agents, player compare
 */
const StandardTile = ({ data, onClick, showTrend }) => {
  const { mlb_id, name, jersey_number, position, team, stats, trend, isPitcher } = data;

  // Get 4 key stats
  const getStandardStats = () => {
    if (!stats) return [];
    
    if (isPitcher) {
      return [
        { value: formatERA(stats.era) || '--', label: 'ERA' },
        { value: formatBattingAvg(stats.whip) || '--', label: 'WHIP' },
        { value: stats.strikeouts_pitched || '--', label: 'K' },
        { value: stats.wins && stats.losses ? `${stats.wins}-${stats.losses}` : '--', label: 'W-L' }
      ];
    } else {
      return [
        { value: formatBattingAvg(stats.batting_avg) || '--', label: 'AVG' },
        { value: stats.home_runs || '--', label: 'HR' },
        { value: stats.rbi || '--', label: 'RBI' },
        { value: stats.stolen_bases || '--', label: 'SB' }
      ];
    }
  };

  const statsList = getStandardStats();

  const containerStyle = {
    display: 'flex',
    alignItems: 'center',
    height: '100px',
    padding: '12px',
    backgroundColor: C.surface[855],
    borderRadius: '8px',
    border: `1px solid ${C.coolGray[850]}`,
    gap: '12px',
    cursor: 'pointer',
    transition: 'all 100ms ease',
    width: '100%'
  };

  const infoContainerStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    flex: 1,
    minWidth: 0
  };

  const topRowStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px'
  };

  const nameRowStyle = {
    display: 'flex',
    alignItems: 'baseline',
    gap: '6px',
    flex: 1,
    minWidth: 0
  };

  const nameStyle = {
    fontSize: '16px',
    fontWeight: '700',
    color: C.white,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  };

  const numberStyle = {
    fontSize: '14px',
    fontWeight: '600',
    color: C.coolGray[400],
    flexShrink: 0
  };

  const detailsStyle = {
    fontSize: '14px',
    fontWeight: '500',
    color: C.coolGray[400]
  };

  const statsRowStyle = {
    display: 'flex',
    gap: '12px',
    fontSize: '14px',
    marginTop: '4px'
  };

  const statItemStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '2px'
  };

  const statValueStyle = {
    fontWeight: '700',
    color: C.white,
    fontSize: '14px'
  };

  const statLabelStyle = {
    fontWeight: '500',
    color: C.coolGray[400],
    fontSize: '11px'
  };

  return (
    <div 
      style={containerStyle}
      onClick={onClick}
      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = C.surface[900]}
      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = C.surface[855]}
    >
      <PlayerPhoto mlbId={mlb_id} size="50x50" />
      
      <div style={infoContainerStyle}>
        <div style={topRowStyle}>
          <div style={nameRowStyle}>
            <span style={nameStyle}>{name}</span>
            {jersey_number && <span style={numberStyle}>#{jersey_number}</span>}
          </div>
          {showTrend && trend && (
            <TrendIndicator 
              trend={trend.status || 'neutral'}
              percentage={trend.percentage || 0}
              size="compact"
            />
          )}
        </div>
        
        <div style={detailsStyle}>
          {position} • {team}
        </div>
        
        <div style={statsRowStyle}>
          {statsList.map((stat, idx) => (
            <div key={idx} style={statItemStyle}>
              <span style={statValueStyle}>{stat.value}</span>
              <span style={statLabelStyle}>{stat.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/**
 * FEATURED VARIANT (200px height)
 * For: Home screen hot players, trending, breakouts
 */
const FeaturedTile = ({ data, onClick, showTrend, showChart }) => {
  const { 
    mlb_id, 
    name, 
    jersey_number, 
    position, 
    team, 
    stats, 
    trend, 
    badge,
    contract,
    percentile,
    isPitcher 
  } = data;

  // Get 4 stats + percentile
  const getFeaturedStats = () => {
    if (!stats) return [];
    
    if (isPitcher) {
      return [
        `${formatERA(stats.era) || '--'} ERA`,
        `${formatBattingAvg(stats.whip) || '--'} WHIP`,
        `${stats.strikeouts_pitched || '--'} K`,
        percentile ? `${percentile}th %ile` : ''
      ].filter(Boolean);
    } else {
      return [
        `${formatBattingAvg(stats.batting_avg) || '--'} AVG`,
        `${stats.home_runs || '--'} HR`,
        `${stats.rbi || '--'} RBI`,
        percentile ? `${percentile}th %ile` : ''
      ].filter(Boolean);
    }
  };

  const statsList = getFeaturedStats();

  const containerStyle = {
    display: 'flex',
    flexDirection: 'column',
    height: '200px',
    padding: '16px',
    background: `linear-gradient(135deg, ${C.surface[855]} 0%, ${C.surface[800]} 100%)`,
    borderRadius: '12px',
    border: `1px solid ${C.gold.bright}`,
    gap: '8px',
    cursor: 'pointer',
    transition: 'all 200ms ease',
    width: '100%',
    boxShadow: `0 4px 12px ${withAlpha(C.black, 0.3)}`
  };

  const headerRowStyle = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px'
  };

  const infoContainerStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    flex: 1,
    minWidth: 0
  };

  const badgeStyle = {
    fontSize: '12px',
    fontWeight: '700',
    color: C.error,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
  };

  const nameStyle = {
    fontSize: '20px',
    fontWeight: '700',
    color: C.white,
    lineHeight: 1.2
  };

  const detailsStyle = {
    fontSize: '14px',
    fontWeight: '500',
    color: C.coolGray[400]
  };

  const chartContainerStyle = {
    height: '60px',
    backgroundColor: withAlpha(C.white, 0.05),
    borderRadius: '8px',
    padding: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: C.coolGray[400],
    fontSize: '12px',
    marginTop: '4px'
  };

  const statsTextStyle = {
    fontSize: '14px',
    fontWeight: '600',
    color: C.white,
    lineHeight: 1.6,
    marginTop: '4px'
  };

  const buttonStyle = {
    padding: '10px 16px',
    backgroundColor: C.gold.bright,
    color: C.surface[800],
    fontSize: '14px',
    fontWeight: '600',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'center',
    marginTop: 'auto',
    transition: 'all 100ms ease'
  };

  const getBadgeConfig = () => {
    if (!badge) return { emoji: '🔥', label: 'TRENDING UP' };
    
    const configs = {
      trending_up: { emoji: '🔥', label: 'TRENDING UP' },
      trending_down: { emoji: '❄️', label: 'TRENDING DOWN' },
      must_add: { emoji: '⭐', label: 'MUST-ADD' },
      breakout: { emoji: '💎', label: 'BREAKOUT' },
      elite: { emoji: '👑', label: 'ELITE' },
      injury_return: { emoji: '🚨', label: 'INJURY RETURN' }
    };
    
    return configs[badge.type] || configs.trending_up;
  };

  const badgeConfig = getBadgeConfig();

  return (
    <div 
      style={containerStyle}
      onClick={onClick}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'scale(1.02)';
        e.currentTarget.style.boxShadow = `0 8px 24px ${withAlpha(C.gold.bright, 0.3)}`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'scale(1)';
        e.currentTarget.style.boxShadow = `0 4px 12px ${withAlpha(C.black, 0.3)}`;
      }}
    >
      <div style={headerRowStyle}>
        <PlayerPhoto mlbId={mlb_id} size="80x80" />
        
        <div style={infoContainerStyle}>
          <div style={badgeStyle}>
            <span>{badgeConfig.emoji}</span>
            <span>{badgeConfig.label}</span>
          </div>
          <div style={nameStyle}>{name} #{jersey_number}</div>
          <div style={detailsStyle}>
            {position} • {team}
            {contract && contract.salary && ` • $${contract.salary}M`}
          </div>
        </div>
      </div>

      {showChart && (
        <div style={chartContainerStyle}>
          {showTrend && trend && (
            <span>Last 14 Days: {trend.percentage > 0 ? '+' : ''}{trend.percentage}% ↗️</span>
          )}
        </div>
      )}

      <div style={statsTextStyle}>
        {statsList.join(' • ')}
      </div>

      <button 
        style={buttonStyle}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = C.gold.hover}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = C.gold.bright}
      >
        View Details →
      </button>
    </div>
  );
};

// Loading style
const loadingStyle = {
  padding: '20px',
  textAlign: 'center',
  color: C.coolGray[400],
  backgroundColor: C.surface[855],
  borderRadius: '8px'
};

MobilePlayerTile.propTypes = {
  variant: PropTypes.oneOf(['compact', 'standard', 'featured']),
  playerId: PropTypes.string.isRequired,
  playerData: PropTypes.shape({
    mlb_id: PropTypes.number,
    name: PropTypes.string.isRequired,
    jersey_number: PropTypes.number,
    position: PropTypes.string,
    team: PropTypes.string,
    stats: PropTypes.object,
    trend: PropTypes.shape({
      status: PropTypes.string,
      percentage: PropTypes.number
    }),
    badge: PropTypes.shape({
      type: PropTypes.string,
      label: PropTypes.string,
      icon: PropTypes.string
    }),
    contract: PropTypes.shape({
      salary: PropTypes.number,
      contract_years: PropTypes.number
    }),
    percentile: PropTypes.number,
    isPitcher: PropTypes.bool
  }).isRequired,
  onClick: PropTypes.func,
  showTrend: PropTypes.bool,
  showChart: PropTypes.bool
};

export default MobilePlayerTile;
